import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { useConversations } from "@/hooks/conversation/useConversations";
import { Conversation } from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationInsightFunnelProps {
  conversation: Conversation;
}

/**
 * Insight funnel: how many of the genie's configured insight fields got captured
 * on this call, plus a per-field capture-rate bar across the last 30 days.
 *
 * Pure SQL aggregate, no AI. Section card lives in the main column on the
 * full-page route. The email template surfaces a one-line summary derived
 * from the same numbers.
 *
 * Brand chrome per guidelines/design.md §6: gradient border + brand-tinted header.
 */
export function ConversationInsightFunnel({ conversation }: ConversationInsightFunnelProps) {
  const agent = conversation.agent || {};
  const agentId = conversation.agent_id;
  const goalFields = agent.insights?.fields || agent.goal?.fields || [];

  const leadInfo = conversation.details?.lead_info || {};

  // 30-day capture rate per field across this genie's conversations.
  const { data, isLoading } = useConversations([agentId], "30d");

  const stats = useMemo(() => {
    const conversations = data?.pages.flatMap((p) => p.data) ?? [];
    const recentCount = conversations.length;

    const rows = [...goalFields]
      .sort((a, b) => Number(!!b.required) - Number(!!a.required))
      .map((field) => ({
        field,
        captured: isCaptured(leadInfo?.[field.key]),
        rate:
          recentCount === 0
            ? 0
            : conversations.filter((c) => isCaptured(c.details?.lead_info?.[field.key])).length / recentCount,
      }));
    const requiredRows = rows.filter((row) => row.field.required);

    return {
      rows,
      caughtThisCall: rows.filter((row) => row.captured).length,
      totalFields: rows.length,
      requiredCaught: requiredRows.filter((row) => row.captured).length,
      requiredTotal: requiredRows.length,
      recentCount,
    };
  }, [data, goalFields, leadInfo]);

  if (stats.totalFields === 0) {
    // Empty state instead of vanishing — the section stays visible so the
    // funnel is discoverable even before capture fields are configured.
    return (
      <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
        <div className="rounded-[14px] bg-white overflow-hidden">
          <div
            className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
            style={{
              background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <GenieIcon
                name="target"
                size={26}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label="Insights funnel"
              />
              <h3 className="text-base font-bold text-gray-900">Insight funnel</h3>
            </div>
          </div>
          <div className="px-5 md:px-6 py-5">
            <p className="text-sm text-gray-600 mb-3">
              No capture fields set up for this genie yet. Add the details you want every call to capture — name,
              phone, job type — and this funnel will show what got caught.
            </p>
            <Link
              to={`/genie-hub/my-genies/${agentId}?section=section-insights`}
              className="inline-flex items-center gap-1 text-sm hover:gap-1.5 transition-all"
            >
              <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
                Add capture fields
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-[#FF4DB8]" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const heroPct = stats.totalFields > 0 ? Math.round((stats.caughtThisCall / stats.totalFields) * 100) : 0;

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <GenieIcon
                name="target"
                size={26}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label="Insights funnel"
              />
              <h3 className="text-base font-bold text-gray-900">Insight funnel</h3>
            </div>
            <Link
              to={`/genie-hub/my-genies/${agentId}?section=section-insights`}
              className="hidden sm:inline-flex items-center gap-1 text-xs hover:gap-1.5 transition-all"
            >
              <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
                Edit insights
              </span>
              <ArrowRight className="w-3 h-3 text-[#FF4DB8]" />
            </Link>
          </div>
        </div>

        <div className="px-5 md:px-6 py-5">
          {/* Hero stat */}
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                  {stats.caughtThisCall} of {stats.totalFields}
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-1 font-medium">insights fields captured on this call</div>
              {stats.requiredTotal > 0 && (
                <div className="text-xs mt-1 font-semibold text-[#FF4DB8]">
                  {stats.requiredCaught} of {stats.requiredTotal} required captured
                </div>
              )}
            </div>
            <div
              className="text-right rounded-lg px-3 py-2 border"
              style={{
                background: "linear-gradient(135deg, rgba(78,156,255,0.06), rgba(255,77,184,0.06))",
                borderColor: "rgba(78,156,255,0.2)",
              }}
            >
              <div className="text-2xl font-extrabold text-gray-900">{heroPct}%</div>
              <div className="text-[10px] text-gray-600 uppercase tracking-wide font-semibold">Capture rate</div>
            </div>
          </div>

          {/* Per-field rows */}
          <div className="space-y-2">
            {stats.rows.map(({ field, captured, rate }) => {
              const ratePct = Math.round(rate * 100);
              return (
                <div
                  key={field.key}
                  className="grid grid-cols-12 gap-3 items-center px-3 py-2 rounded-lg"
                  style={{ background: "rgba(78,156,255,0.03)" }}
                >
                  <div className="col-span-5 flex items-center gap-2 min-w-0">
                    {captured ? (
                      <span className="inline-flex shrink-0 items-center justify-center w-5 h-5 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </span>
                    ) : (
                      <span className="inline-flex shrink-0 items-center justify-center w-5 h-5 rounded-full bg-gray-200">
                        <X className="w-3 h-3 text-gray-500" strokeWidth={3} />
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-900 truncate" title={field.label}>
                      {field.label}
                    </span>
                    {field.required && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#FF4DB8]/10 text-[#FF4DB8] flex-shrink-0">
                        Required
                      </span>
                    )}
                  </div>
                  <div className="col-span-7 flex items-center gap-3">
                    {isLoading ? (
                      <div className="h-2 flex-1 rounded-full bg-gray-100 animate-pulse" />
                    ) : (
                      <>
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]"
                            style={{ width: `${Math.max(2, ratePct)}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-gray-700 tabular-nums w-10 text-right">
                          {ratePct}%
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!isLoading && stats.recentCount > 0 && (
            <div className="text-[11px] text-gray-500 mt-3 uppercase tracking-wide font-semibold">
              Capture rate across last {stats.recentCount} calls (30 days)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function isCaptured(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return Boolean(value);
}
